import { useEffect, useRef } from 'react';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: black; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">☕</div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

const locations = [
  {
    id: 1,
    name: 'Honey I\'m Home Coffee',
    address: '451 High Road',
    city: 'Woodford Green, London IG8 0XE',
    phone: '+44 7739 083131',
    email: 'Enquiries@honeyimhomecoffee.com',
    hours: {
      tuesday_friday: '8:00 AM - 5:00 PM',
      saturday_sunday: '8:30 AM - 5:00 PM',
      monday: 'Closed (Temporarily)'
    },
    coordinates: [51.6075, 0.0235],
    flagship: true
  }
];

// Center on Woodford Green
const mapCenter = [51.6075, 0.0235];

export default function LocationPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="location-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF5]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div
            
            
            
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Find Us</p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
              Our <span className="italic">Locations</span>
            </h1>
            <p className="text-neutral-600 text-lg">
              Visit us at any of our London locations. Each space is designed to feel like home, 
              with the same exceptional coffee and warm hospitality.
            </p>
          </div>
        </div>
      </section>

      {/* Map and Locations */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Locations List */}
            <div className="lg:col-span-1 space-y-6" data-testid="locations-list">
              {locations.map((location, index) => (
                <div
                  key={location.id}
                  
                  
                  
                  className={`p-6 border ${location.flagship ? 'border-black' : 'border-neutral-200'} bg-white hover:shadow-lg transition-shadow`}
                  data-testid={`location-card-${location.id}`}
                >
                  {location.flagship && (
                    <span className="text-[10px] tracking-wider uppercase bg-black text-white px-2 py-1 mb-4 inline-block">
                      Flagship Store
                    </span>
                  )}
                  <h3 className="font-serif text-xl font-semibold text-black mb-4">{location.name}</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start">
                      <MapPin size={18} className="mr-3 text-neutral-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-neutral-700">{location.address}</p>
                        <p className="text-neutral-700">{location.city}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Phone size={18} className="mr-3 text-neutral-400 flex-shrink-0" />
                      <a href={`tel:${location.phone}`} className="text-neutral-700 hover:text-black">
                        {location.phone}
                      </a>
                    </div>
                    
                    <div className="flex items-start">
                      <Clock size={18} className="mr-3 text-neutral-400 flex-shrink-0 mt-0.5" />
                      <div className="text-neutral-700">
                        <p className="text-red-600 font-medium">Mon: {location.hours.monday}</p>
                        <p>Tue-Fri: {location.hours.tuesday_friday}</p>
                        <p>Sat-Sun: {location.hours.saturday_sunday}</p>
                      </div>
                    </div>
                  </div>
                  
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${location.coordinates[0]},${location.coordinates[1]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center text-black font-medium text-sm hover:underline"
                    data-testid={`directions-btn-${location.id}`}
                  >
                    <Navigation size={16} className="mr-2" />
                    Get Directions
                  </a>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="lg:col-span-2 h-[500px] lg:h-auto min-h-[500px]" data-testid="locations-map">
              <MapContainer
                center={mapCenter}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {locations.map((location) => (
                  <Marker
                    key={location.id}
                    position={location.coordinates}
                    icon={customIcon}
                  >
                    <Popup>
                      <div className="p-2">
                        <h4 className="font-semibold mb-1">{location.name}</h4>
                        <p className="text-sm text-neutral-600">{location.address}</p>
                        <p className="text-sm text-neutral-600">{location.city}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div
              
               
              
            >
              <div className="text-4xl mb-4">🚗</div>
              <h3 className="font-serif text-xl font-semibold mb-2">Parking</h3>
              <p className="text-neutral-400 text-sm">
                Street parking available. Check local signage for restrictions.
              </p>
            </div>
            
            <div
              
               
              
              
            >
              <div className="text-4xl mb-4">🚇</div>
              <h3 className="font-serif text-xl font-semibold mb-2">Public Transport</h3>
              <p className="text-neutral-400 text-sm">
                All locations are within walking distance of tube stations.
              </p>
            </div>
            
            <div
              
               
              
              
            >
              <div className="text-4xl mb-4">♿</div>
              <h3 className="font-serif text-xl font-semibold mb-2">Accessibility</h3>
              <p className="text-neutral-400 text-sm">
                All locations are wheelchair accessible with accessible restrooms.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
